/**
 * When a submitted shadow review reaches its reviewee, and until when its
 * reviewer may take it back. A submission is held for a grace period before
 * delivery, and the reviewer may undo it only while it is held, so nothing
 * reviewee-facing ever has to be recalled. Kept free of Firestore so the
 * functions and the review page apply one rule.
 */

/**
 * How long a submitted review is held before it is delivered to the reviewee
 * (see {@link getShadowReleaseAtMs}).
 */
export const SHADOW_DELIVERY_GRACE_MS = 30 * 60 * 1000;

/** Why a submitted review cannot be taken back. */
export const SHADOW_UNSUBMIT_REFUSALS = [
  /** The feedback is not submitted (already a draft, or missing). */
  'not-submitted',
  /** The submit trigger has not finished recording the submission yet. */
  'processing',
  /** The grace period has ended, so the review is being or has been delivered. */
  'window-closed',
  /** Course staff decided the review's moderation; their decision stands. */
  'staff-moderated',
  /** The review's thread has a reply. */
  'replied',
] as const;

/** Why a submitted review cannot be taken back. */
export type ShadowUnsubmitRefusal = (typeof SHADOW_UNSUBMIT_REFUSALS)[number];

/**
 * When a review submitted at `submittedAtMs` is released to its reviewee:
 * `submittedAtMs + SHADOW_DELIVERY_GRACE_MS`.
 *
 * @param submittedAtMs - When the reviewer submitted (epoch ms).
 * @return The release instant (epoch ms).
 * @throws Never.
 */
export function getShadowReleaseAtMs({
  submittedAtMs,
}: {
  submittedAtMs: number;
}): number {
  return submittedAtMs + SHADOW_DELIVERY_GRACE_MS;
}

/**
 * Whether a submitted review is still held: true on the half-open interval
 * `[submittedAt, releaseAt)`, while it may be undone. From `releaseAt` itself
 * it is due for delivery.
 *
 * @param releaseAtMs - The review's release instant (epoch ms).
 * @param nowMs - Current time (epoch ms).
 * @return True while `nowMs` is before `releaseAtMs`.
 * @throws Never.
 */
export function isShadowReleasePending({
  releaseAtMs,
  nowMs,
}: {
  releaseAtMs: number;
  nowMs: number;
}): boolean {
  return nowMs < releaseAtMs;
}

/**
 * Decides whether a reviewer may take back their submitted review.
 *
 * @param feedbackStatus - The feedback doc's status; undefined when it is
 *   missing.
 * @param assignmentStatus - The authorizing assignment's status; undefined
 *   when it is missing. The submit trigger marks it submitted last, so until
 *   then the submission is still being recorded.
 * @param releaseAtMs - When the review is released to the reviewee (epoch
 *   ms); undefined for a review submitted before releases were held, which
 *   was delivered on submit and so can never be undone.
 * @param nowMs - Current time (epoch ms).
 * @param isStaffModerated - True when course staff approved or rejected the
 *   review by hand.
 * @param hasReplies - True when the review's thread has any reply.
 * @return The first refusal that applies, or null when the review may be taken
 *   back.
 * @throws Never.
 */
export function getShadowUnsubmitRefusal({
  feedbackStatus,
  assignmentStatus,
  releaseAtMs,
  nowMs,
  isStaffModerated,
  hasReplies,
}: {
  feedbackStatus: string | undefined;
  assignmentStatus: string | undefined;
  releaseAtMs: number | undefined;
  nowMs: number;
  isStaffModerated: boolean;
  hasReplies: boolean;
}): ShadowUnsubmitRefusal | null {
  if (feedbackStatus !== 'submitted') return 'not-submitted';
  if (assignmentStatus !== 'submitted') return 'processing';
  if (
    releaseAtMs === undefined ||
    !isShadowReleasePending({releaseAtMs, nowMs})
  ) {
    return 'window-closed';
  }
  if (isStaffModerated) return 'staff-moderated';
  if (hasReplies) return 'replied';
  return null;
}
